import { HubFeature } from '@ats/api/cm';
import { LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';
import { selfMergeDPRoot } from '../self-merge-route.locs';

const feature = HubFeature.MergeCandidatesCandidateSelfMerge;

export type SelfMergeLocs =
	| 'CPSelfMergeSearchSectionTitle'
	| 'CPSelfMergeSearchLabel'
	| 'CPSelfMergeEmailAddressLabel'
	| 'CPSelfMergeIDLabel'
	| 'CPSelfMergeEmailRequired'
	| 'CPSelfMergeIDRequired'
	| 'CPSelfMergeEmailInvalid'
	| 'CPSelfMergeExternalIdInvalid'
	| 'CPSelfMergeEmailAccessConfirmation'
	| 'CPSelfMergeSubmit'
	| 'CPSelfMergeSuccessCheckEmail'
	| 'CPSelfMergeSuccessContactSupport'
	| 'CPSelfMergeSuccessNotification'
	| 'CPSelfMergeEmailAccessError'
	| 'CPSelfMergePendingMerge'
	| 'CPSelfMergeMultipleRecords'
	| 'CPSelfMergeNoRecords'
	| 'CPSelfMergeInvalidRequest'
	| 'CPSelfMergeUniqueLoser'
	| 'CPSelfMergeMismatchingAccounts'
	| 'CPSelfMergeExamActivity'
	| 'CPSelfMergeUnidentifiedError';

export const selfMergeLocs: LocDefs<SelfMergeLocs> = {
	CPSelfMergeSearchSectionTitle: {
		defaultValue: 'Request Account Merge',
		description: 'Title for the panel containing the merge request form.',
		displayPath: `${selfMergeDPRoot}|Section Title`,
		displayType: 'Title',
		feature,
		portal: 'CP',
		htmlAllowed: false,
		shared: false,
	},
	CPSelfMergeSearchLabel: {
		defaultValue: 'Search By',
		description: 'Label for the self merge search by dropdown.',
		displayPath: `${selfMergeDPRoot}|Search By Label`,
		displayType: 'Label',
		feature,
		portal: 'CP',
		htmlAllowed: false,
		shared: false,
	},
	CPSelfMergeEmailAddressLabel: {
		defaultValue: 'Email Address',
		description: 'Label for the email address field.',
		displayPath: `${selfMergeDPRoot}|Email Address Label`,
		displayType: 'Label',
		feature,
		portal: 'CP',
	},
	CPSelfMergeIDLabel: {
		defaultValue: 'Username',
		description: 'Label for the username field.',
		displayPath: `${selfMergeDPRoot}|Username Label`,
		displayType: 'Label',
		feature,
		portal: 'CP',
		htmlAllowed: false,
		shared: false,
	},
	CPSelfMergeEmailRequired: {
		defaultValue: 'Email is required',
		description: 'Error displayed when email is not provided.',
		displayPath: `${selfMergeDPRoot}|Email Required`,
		displayType: 'Error',
		feature,
		portal: 'CP',
		htmlAllowed: false,
		shared: false,
	},
	CPSelfMergeIDRequired: {
		defaultValue: 'Username is required',
		description: 'Error displayed when username is not provided.',
		displayPath: `${selfMergeDPRoot}|Username Required`,
		displayType: 'Error',
		feature,
		portal: 'CP',
		htmlAllowed: false,
		shared: false,
	},
	CPSelfMergeEmailInvalid: {
		defaultValue: 'Invalid email address',
		description: 'Error displayed when email is invalid.',
		displayPath: `${selfMergeDPRoot}|Invalid Email`,
		displayType: 'Error',
		feature,
		portal: 'CP',
		htmlAllowed: false,
		shared: false,
	},
	CPSelfMergeExternalIdInvalid: {
		defaultValue: 'Invalid username',
		description: 'Error displayed when the candidate puts in their own username.',
		displayPath: `${selfMergeDPRoot}|Invalid Username`,
		displayType: 'Error',
		feature,
		portal: 'CP',
		htmlAllowed: false,
		shared: false,
	},
	CPSelfMergeEmailAccessConfirmation: {
		defaultValue: 'I have access to the email address associated with this account.',
		description:
			'Checkbox for candidate to confirm they can access the email address associated with the account they are attempting to merge.',
		displayPath: `${selfMergeDPRoot}|Access to Email Label`,
		displayType: 'Content',
		feature,
		portal: 'CP',
		htmlAllowed: false,
		shared: false,
	},
	CPSelfMergeSubmit: {
		defaultValue: 'Request Account Merge',
		description: 'Text displayed on the form submit button.',
		displayPath: `${selfMergeDPRoot}|Submit Button`,
		displayType: 'Button',
		feature,
		portal: 'CP',
		htmlAllowed: false,
		shared: false,
	},
	CPSelfMergeSuccessCheckEmail: {
		defaultValue: 'Please check the email associated with the account to complete the merge.',
		description: 'Message displayed after requesting a merge with access to email address.',
		displayPath: `${selfMergeDPRoot}|Success - Check Email`,
		displayType: 'Content',
		feature,
		portal: 'CP',
		htmlAllowed: false,
		shared: false,
	},
	CPSelfMergeSuccessContactSupport: {
		defaultValue:
			'A merge for this account has been requested. If you need any further help, please contact support.',
		description: 'Message displayed after requesting a merge with no access to email address.',
		displayPath: `${selfMergeDPRoot}|Success - Contact Support`,
		displayType: 'Content',
		feature,
		portal: 'CP',
		htmlAllowed: false,
		shared: false,
	},
	CPSelfMergeSuccessNotification: {
		defaultValue: 'Merge requested.',
		description: 'Notification shown on successful submission of the request form.',
		displayPath: `${selfMergeDPRoot}|Success Notification`,
		displayType: 'Notification',
		feature,
		portal: 'CP',
	},
	CPSelfMergeEmailAccessError: {
		defaultValue:
			'You must have access to the email address associated with this account to request a merge.',
		description:
			'Error displayed when the candidate attempts to request a merge without checking the email access confirmation checkbox.',
		displayPath: `${selfMergeDPRoot}|Email Access Error`,
		displayType: 'Error',
		feature: HubFeature.MergeCandidatesCandidateSelfMergeAwsCustomSelfMergeSecurity,
		portal: 'CP',
		htmlAllowed: false,
		shared: false,
	},
	CPSelfMergePendingMerge: {
		defaultValue: 'Merge requested.',
		description: 'Message displayed when a merge is already pending.',
		displayPath: `${selfMergeDPRoot}|Errors|Pending Request`,
		displayType: 'Error',
		portal: 'CP',
		legacyName:
			'MergeCandidates.CandidateSelfMerge/Error.GUID.189cceb95a5a4d30b5be199e954cf392',
		feature,
		htmlAllowed: false,
		shared: false,
	},
	CPSelfMergeMultipleRecords: {
		defaultValue: 'Merge requested.',
		description: 'Message displayed when the account search yields too many results.',
		displayPath: `${selfMergeDPRoot}|Errors|Multiple Accounts Found`,
		displayType: 'Error',
		portal: 'CP',
		legacyName:
			'MergeCandidates.CandidateSelfMerge/Error.GUID.6c05c657acce4a029025b8d298cac82c',
		feature,
		htmlAllowed: false,
		shared: false,
	},
	CPSelfMergeNoRecords: {
		defaultValue: 'Merge requested.',
		description: 'Message displayed when the account search yields no results.',
		displayPath: `${selfMergeDPRoot}|Errors|No Account Found`,
		displayType: 'Error',
		portal: 'CP',
		legacyName:
			'MergeCandidates.CandidateSelfMerge/Error.GUID.bf537a3fa9424bbf85b9b74759585a6a',
		feature,
		htmlAllowed: false,
		shared: false,
	},
	CPSelfMergeInvalidRequest: {
		defaultValue: 'Merge requested.',
		description: 'Message displayed when the request is invalid.',
		displayPath: `${selfMergeDPRoot}|Errors|Invalid Request`,
		displayType: 'Error',
		portal: 'CP',
		legacyName:
			'MergeCandidates.CandidateSelfMerge/Error.GUID.4342d73bf32943d5a5061b145e12d16d',
		feature,
		htmlAllowed: false,
		shared: false,
	},
	CPSelfMergeUniqueLoser: {
		defaultValue: 'Merge requested.',
		description: 'Message displayed when requested candidate to merge into is unique.',
		displayPath: `${selfMergeDPRoot}|Errors|Unique Loser`,
		displayType: 'Error',
		portal: 'CP',
		feature,
		htmlAllowed: false,
		shared: false,
	},
	CPSelfMergeMismatchingAccounts: {
		defaultValue: 'Merge requested.',
		description: 'Message displayed when the candidate accounts do not match.',
		displayPath: `${selfMergeDPRoot}|Errors|Mismatching Accounts`,
		displayType: 'Error',
		portal: 'CP',
		feature,
		htmlAllowed: false,
		shared: false,
	},
	CPSelfMergeExamActivity: {
		defaultValue: 'Merge requested.',
		description:
			'Message displayed when both accounts have exam activity and cannot be merged.',
		displayPath: `${selfMergeDPRoot}|Errors|Exam Activity`,
		displayType: 'Error',
		portal: 'CP',
		feature: HubFeature.MergeCandidatesCandidateSelfMergeAwsCustomSelfMergeSecurity,
		htmlAllowed: false,
		shared: false,
	},
	CPSelfMergeUnidentifiedError: {
		defaultValue: 'Merge requested.',
		description: 'Message displayed when an unidentified error occurs.',
		displayPath: `${selfMergeDPRoot}|Errors|Unidentified Error`,
		displayType: 'Error',
		portal: 'CP',
		feature,
		htmlAllowed: false,
		shared: false,
	},
};

export type SelfMergeLocType = LocMap<SelfMergeLocs>;
export const LOC_KEYS = MapLocKeys<SelfMergeLocs>(selfMergeLocs);

export const mapSelfMergeErrorToLoc = (id?: string): SelfMergeLocs => {
	switch (id) {
		case '189cceb95a5a4d30b5be199e954cf392':
			return 'CPSelfMergePendingMerge';
		case '6c05c657acce4a029025b8d298cac82c':
			return 'CPSelfMergeMultipleRecords';
		case 'bf537a3fa9424bbf85b9b74759585a6a':
			return 'CPSelfMergeNoRecords';
		case '4342d73bf32943d5a5061b145e12d16d':
			return 'CPSelfMergeInvalidRequest';
		case 'c04a632db9d14465a6c896a065956446':
			return 'CPSelfMergeUniqueLoser';
		case '3ce77893f90d41d198b40a76ec1ab79b':
			return 'CPSelfMergeMismatchingAccounts';
		case '5ee1df0b2e0245cfbb3fbed3730b28ef':
			return 'CPSelfMergeExamActivity';
		default:
			return 'CPSelfMergeUnidentifiedError';
	}
};
